import { defineStore } from 'pinia';

import type { EmailSettings, Mailbox } from '@stuut/api-sdk/models';

import { sdk } from '@/sdk';

interface State {
  loaded: boolean;
  loadError: string | null;
  loading: boolean;
  mailboxes: Mailbox[];
  settings: EmailSettings | null;
}

/**
 * Shared mailbox + email-settings state. Single source of truth so every consumer
 * (the settings page's MailboxManager and the onboarding mailboxes step) reads the
 * same list — connect/disconnect in one place reflects everywhere via `load()`.
 */
export const useMailboxesStore = defineStore('mailboxes', {
  state: (): State => ({
    mailboxes: [],
    settings: null,
    loading: false,
    loadError: null,
    loaded: false,
  }),
  getters: {
    // Archived (disconnected) mailboxes are kept server-side for historical email
    // attribution but aren't manageable senders.
    activeMailboxes: (state) => state.mailboxes.filter((m) => !m.archivedAt),
    connectedMailboxes(): Mailbox[] {
      return this.activeMailboxes.filter((m) => m.status === 'connected');
    },
    hasConnectedMailbox(): boolean {
      return this.connectedMailboxes.length > 0;
    },
  },
  actions: {
    async load() {
      this.loading = true;
      this.loadError = null;
      try {
        const [settings, mailboxes] = await Promise.all([
          sdk.mailboxSettings.get(),
          sdk.mailboxes.list(),
        ]);
        this.settings = settings;
        this.mailboxes = mailboxes;
        this.loaded = true;
      } catch (error) {
        this.loadError =
          error instanceof Error ? error.message : 'Failed to load mailbox settings.';
        throw error;
      } finally {
        this.loading = false;
      }
    },
  },
});
